import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

import WikiEmbed from '../WikiEmbed/WikiEmbed';

import './Markdown.module.scss'; // FIXME: import as styles?

const ABSOLUTE_URL_REGEX = /^(?:https?:)?\/\//i;

// FAMILY-EXT: распознаём [[wiki:path/to/page]] в исходном markdown.
// Регекс не жадный, чтобы поймать несколько embed'ов в одном тексте.
// Path: всё, что не `]` и не перенос строки.
const WIKI_EMBED_REGEX = /\[\[wiki:([^\]\n]+)\]\]/g;

// Разрезает строку на массив { kind: 'text'|'wiki', value }.
function splitByWikiEmbeds(text) {
  if (typeof text !== 'string' || text.length === 0) return [{ kind: 'text', value: text || '' }];
  const result = [];
  let lastIndex = 0;
  const re = new RegExp(WIKI_EMBED_REGEX);
  let match = re.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      result.push({ kind: 'text', value: text.slice(lastIndex, match.index) });
    }
    result.push({ kind: 'wiki', value: match[1].trim() });
    lastIndex = match.index + match[0].length;
    match = re.exec(text);
  }
  if (lastIndex < text.length) {
    result.push({ kind: 'text', value: text.slice(lastIndex) });
  }
  return result;
}

const Markdown = React.memo(({ children, linkStopPropagation, ...props }) => {
  const handleLinkClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const linkRenderer = useCallback(
    ({ node, ...linkProps }) => (
      /* eslint-disable-next-line jsx-a11y/anchor-has-content,
                                  jsx-a11y/click-events-have-key-events,
                                  jsx-a11y/no-static-element-interactions */
      <a
        {...linkProps} // eslint-disable-line react/jsx-props-no-spreading
        rel={
          ABSOLUTE_URL_REGEX.test(linkProps.href) && linkProps.target === '_blank'
            ? 'noreferrer'
            : undefined
        }
        onClick={linkStopPropagation ? handleLinkClick : undefined}
      />
    ),
    [linkStopPropagation, handleLinkClick],
  );

  // FAMILY-EXT: разбиваем markdown на текстовые сегменты и wiki-embed'ы.
  // Текст рендерим обычным ReactMarkdown, embed'ы — компонентом WikiEmbed.
  const segments = useMemo(() => splitByWikiEmbeds(children), [children]);

  // Если ни одного [[wiki:...]] не было — старый путь (один ReactMarkdown)
  if (segments.length === 1 && segments[0].kind === 'text') {
    return (
      <ReactMarkdown
        {...props} // eslint-disable-line react/jsx-props-no-spreading
        components={{
          a: linkRenderer,
        }}
        remarkPlugins={[remarkGfm, remarkBreaks]}
        className="markdown-body"
      >
        {segments[0].value}
      </ReactMarkdown>
    );
  }

  return (
    <div className="markdown-body">
      {segments.map((seg, idx) => {
        if (seg.kind === 'wiki') {
          // eslint-disable-next-line react/no-array-index-key
          return <WikiEmbed key={`wiki-${idx}-${seg.value}`} path={seg.value} />;
        }
        if (!seg.value.trim()) return null;
        return (
          <ReactMarkdown
            // eslint-disable-next-line react/no-array-index-key
            key={`md-${idx}`}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
            components={{
              a: linkRenderer,
            }}
            remarkPlugins={[remarkGfm, remarkBreaks]}
          >
            {seg.value}
          </ReactMarkdown>
        );
      })}
    </div>
  );
});

Markdown.propTypes = {
  children: PropTypes.string,
  linkStopPropagation: PropTypes.bool,
};

Markdown.defaultProps = {
  children: '',
  linkStopPropagation: false,
};

export default Markdown;
